import * as React from 'react'

import { ExternalLinkIcon } from 'lucide-react'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList, toggleClass } from 'react-classlist-helper'

import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  getSharedProjectName,
  SharedInfo,
} from '~/client/src/shared/models/SharedInfo'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import PermitCardFieldsConfigStore, {
  ICardAttribute,
  MAX_NAME_LENGTH,
} from '~/client/src/shared/stores/ui/PermitCardFieldsConfig.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { truncateText } from '~/client/src/shared/utils/textUtils'
import { areObjectsEqual } from '~/client/src/shared/utils/util'

import WorkflowTypeIcon from '../../../WorkflowTypeIcon/WorkflowTypeIcon'
import PermitCalendarCardStore, {
  CARD_ROW_HEIGHT,
  MIN_CARD_TITLE_ROW_HEIGHT,
  MIN_CARD_WIDTH,
} from './PermitCalendarCard.store'

import Colors from '~/client/src/shared/theme.module.scss'
import './PermitCalendarCard.scss'

interface IProps {
  permit: Workflow
  backgroundColor: string
  color: string
  border: string
  isRotated: boolean
  containerHeight: number
  containerWidth: number

  className?: string
  shouldUseMockValues?: boolean
  isShared?: boolean
  sharedInfo?: SharedInfo

  setIgnoreTooltip?(isIgnored: boolean): void

  projectDateStore?: ProjectDateStore
  companiesStore?: CompaniesStore
  workflowTypesStore?: WorkflowTypesStore
  locationAttributesStore?: LocationAttributesStore
  projectsStore?: ProjectsStore
}

@inject(
  'projectDateStore',
  'companiesStore',
  'workflowTypesStore',
  'locationAttributesStore',
  'projectsStore',
)
@observer
export default class PermitCalendarCard extends React.Component<IProps> {
  @observable private containerWidth: number = 0
  @observable private containerHeight: number = 0
  @observable private permit: Workflow = null
  @observable private shouldUseMockValues: boolean = null
  private permitTitleContainer: HTMLDivElement
  private readonly store: PermitCalendarCardStore
  private readonly cardFieldsStore: PermitCardFieldsConfigStore

  public constructor(props: IProps) {
    super(props)

    this.permit = props.permit
    this.containerWidth = props.containerWidth || 0
    this.containerHeight = props.containerHeight || 0
    this.shouldUseMockValues = props.shouldUseMockValues

    this.store = new PermitCalendarCardStore()

    this.cardFieldsStore = new PermitCardFieldsConfigStore(
      props.permit,
      props.projectDateStore,
      props.companiesStore,
      props.workflowTypesStore,
    )
    makeObservable(this)
  }

  public componentDidMount(): void {
    this.store.setSharedLocationLink(this.props.sharedInfo?.locationUrl)
  }

  public componentDidUpdate(prevProps: IProps) {
    runInAction(() => {
      const { permit, containerWidth, containerHeight, shouldUseMockValues } =
        this.props
      if (!areObjectsEqual(this.permit, permit)) {
        this.permit = permit
      }
      if (this.containerWidth !== containerWidth) {
        this.containerWidth = containerWidth
      }
      if (this.containerHeight !== containerHeight) {
        this.containerHeight = containerHeight
      }
      if (this.shouldUseMockValues !== shouldUseMockValues) {
        this.shouldUseMockValues = shouldUseMockValues
      }

      if (permit !== prevProps.permit) {
        this.cardFieldsStore.init(permit)
      }

      this.changeStatusRowAlignment()
    })
  }

  public render() {
    const {
      permit,
      backgroundColor: propsBackgroundColor,
      border,
      isRotated,
      className,
      isShared,
    } = this.props
    const backgroundColor = isShared ? Colors.neutral100 : propsBackgroundColor

    return (
      <div
        className={classList({
          'form-calendar-card full-height full-width px5 brada4': true,
          [className]: !!className,
          opacity7: permit.isDone && !isShared,
          opacity6: permit.isDeniedOrFailed && !isShared,
          'shared-permit-card': isShared,
          'draft-workflow': permit.isDraft && !isShared,
        })}
        style={{ border: !isShared ? border : undefined }}
      >
        <div
          className="event-card-background full-width full-height"
          style={{ backgroundColor }}
        />
        <div
          className={toggleClass('rotated-event-label relative pl4', isRotated)}
        >
          <div className="row" ref={this.setPermitTitleContainerRef}>
            <div
              className={classList({
                'relative row full-width': true,
                wrap: !isRotated,
              })}
            >
              {isShared ? this.sharedPermitTitle : this.title}
            </div>
          </div>
          <div
            className={classList({
              'no-grow text large grey-light': true,
              'white-space-normal': this.shouldCombineInfoIntoRow,
              col: !this.shouldCombineInfoIntoRow && !isRotated,
            })}
          >
            {!isRotated && (
              <>
                {this.renderLocationRow()}
                {this.equipmentRow}
              </>
            )}
            {this.attributeValueElements}
          </div>
        </div>
      </div>
    )
  }

  private get sharedPermitTitle(): JSX.Element {
    const { sharedInfo, permit, projectsStore } = this.props
    const { getFieldValueById } = this.cardFieldsStore

    return (
      <div className="col full-width">
        <div className="row full-width">
          {this.formTypeIconElement}
          <div
            style={{ minWidth: '80px' }}
            className="text large light mr4 text-ellipsis"
          >
            {getSharedProjectName(
              sharedInfo?.projectUrl,
              projectsStore,
              sharedInfo?.projectFromId,
            )}
          </div>
          <div className="permit-shared-icon-holder no-grow">
            <ExternalLinkIcon
              size={16}
              className="permit-shared-icon tw:text-background"
            />
          </div>
        </div>
        <span className="text large light mr4 white-space-normal">
          {Localization.translator.shared} {permit.title}
        </span>
        <span className="text large light mr4 text-ellipsis">
          {getFieldValueById(
            FieldIds.BOOKING_TIME,
            this.props.shouldUseMockValues,
          )}
        </span>
        <span className="text large light mr4 text-ellipsis">
          {sharedInfo?.companyName}
        </span>
        {sharedInfo?.companyName && (
          <span className="mr4">
            <StatusBadge
              status={permit.status}
              workflowStepLevel={permit.workflowStepLevel}
              isLate={permit.isLate}
              className="tw:mr-1"
              compact
            />
          </span>
        )}
      </div>
    )
  }

  private get title(): JSX.Element[] {
    const { shouldUseMockValues, permit, workflowTypesStore } = this.props
    const { shouldAlignStatusToEnd } = this.store

    const { titleCardFields, getFieldValueById } = this.cardFieldsStore

    return titleCardFields.map((f, index) => {
      const fieldValue = getFieldValueById(f.fieldId, shouldUseMockValues)
      const displayValue = Array.isArray(fieldValue)
        ? fieldValue.join(', ')
        : fieldValue
      const truncatedFieldValue = truncateText(displayValue, MAX_NAME_LENGTH)
      const isStatusField = f.fieldId === FieldIds.STATUS
      const isCompanyField = f.fieldId === FieldIds.COMPANY
      const shouldUseNoGrowClass =
        !isStatusField || !index || (isStatusField && !shouldAlignStatusToEnd)

      return (
        <React.Fragment key={f.fieldId}>
          <div
            className={classList({
              row: true,
              'no-grow': shouldUseNoGrowClass,
            })}
          >
            {!index && this.formTypeIconElement}
            {isStatusField ? (
              <StatusBadge
                status={permit.status}
                workflowStepLevel={permit.workflowStepLevel}
                isLate={permit.isLate}
                className="tw:mr-1"
                compact
              />
            ) : (
              <span
                title={displayValue}
                className="mr5 text large bold brand-dark"
              >
                {truncatedFieldValue}
              </span>
            )}
          </div>
          {isCompanyField && (
            <div
              style={{ minWidth: '120px' }}
              className="text large light mr4 text-ellipsis"
            >
              {permit.getCaption(workflowTypesStore)}
            </div>
          )}
        </React.Fragment>
      )
    })
  }

  private get attributeValueElements(): JSX.Element[] {
    const { titleCardFields } = this.cardFieldsStore
    const isTitleRowEmpty = !titleCardFields?.length

    return this.cardAttributeValues.map((value, index) => (
      <React.Fragment key={value.fieldId}>
        {this.renderAttributeField(value, !!index, isTitleRowEmpty && !index)}
      </React.Fragment>
    ))
  }

  private renderLocationRow() {
    const { permit, locationAttributesStore } = this.props

    const dtos = permit.locations
      .map(l => locationAttributesStore.getLocationBySiteLocationObj(l))
      .filter(dto => dto)

    const isTitleRowEmpty = !this.cardFieldsStore.titleCardFields?.length
    return dtos.map((value, index) => (
      <React.Fragment key={value.id + index}>
        {this.renderAttributeField(
          { fieldValue: value.name, fieldId: value.id, isBold: false },
          !!index,
          isTitleRowEmpty && !index,
        )}
      </React.Fragment>
    ))
  }

  private get equipmentRow(): JSX.Element[] {
    const { permit, locationAttributesStore } = this.props

    const dtos = permit.equipment
      .map(l => locationAttributesStore.getLocationBySiteLocationObj(l))
      .filter(dto => dto)

    return dtos.map((value, index) => (
      <React.Fragment key={value.id}>
        {this.renderAttributeField(
          { fieldValue: value.name, fieldId: value.id, isBold: false },
          !!index,
          false,
        )}
      </React.Fragment>
    ))
  }

  private renderAttributeField = (
    field: ICardAttribute,
    shouldAddDot: boolean,
    shouldAddIcon: boolean,
  ): JSX.Element => {
    const iconContent = shouldAddIcon && this.formTypeIconElement

    if (!this.shouldCombineInfoIntoRow && !this.props.isRotated) {
      return (
        <span
          title={field.fieldValue}
          className={classList({
            'text large grey-light': true,
            bold: field.isBold,
          })}
        >
          {iconContent}
          {field.fieldValue}
        </span>
      )
    }

    return (
      <>
        {iconContent}
        {shouldAddDot && ' • '}
        {field.isBold ? <b>{field.fieldValue}</b> : field.fieldValue}
      </>
    )
  }

  private get formTypeIconElement(): JSX.Element {
    const { permit, workflowTypesStore, isShared } = this.props
    return (
      <WorkflowTypeIcon
        iconName={permit.getIconNameOfPermitType(workflowTypesStore)}
        size="small"
        className={classList({
          'tw:mr-1 permit-type-icon': true,
          'shared-permit-main-icon': isShared,
        })}
      />
    )
  }

  @action.bound
  private changeStatusRowAlignment() {
    if (this.permitTitleContainer) {
      const { scrollHeight } = this.permitTitleContainer
      this.store.setStatusAlignment(scrollHeight < MIN_CARD_TITLE_ROW_HEIGHT)
    }
  }

  private setPermitTitleContainerRef = (ref: HTMLDivElement) => {
    this.permitTitleContainer = ref
  }

  @computed
  private get cardAttributeValues(): ICardAttribute[] {
    return this.cardFieldsStore.getCardAttributeValues(this.shouldUseMockValues)
  }

  @computed
  private get shouldCombineInfoIntoRow(): boolean {
    const scrollHeight = this.permitTitleContainer?.scrollHeight || 0
    const arrtibutesLength =
      this.permit.locationsWithEquipment.length +
      this.cardAttributeValues.length
    const attributesHeight = arrtibutesLength * CARD_ROW_HEIGHT + scrollHeight

    return (
      attributesHeight > this.containerHeight &&
      this.containerWidth > MIN_CARD_WIDTH
    )
  }
}
